import { useState, useEffect, useRef, Suspense, useSyncExternalStore, type CSSProperties, type ReactNode } from 'react';
import { BrowserRouter as Router, Routes, Route, Link, useNavigate, useLocation, Navigate, useParams } from 'react-router-dom';
import Login from './components/Login';
import ErrorBoundary from './components/ErrorBoundary';
import { getToken, setToken, removeToken, getMember, removeMember, setMember, isAuthenticated, subscribeAuthExpired, consumeAuthExpiredMessage, isAdmin, isKioskMode, getKioskKind, getKioskTournamentId, hasMemberRole, type KioskKind } from './utils/auth';
import { enterKioskMode, defaultKioskKindForRoles } from './utils/kioskEntry';
import api from './utils/api';
import { connectSocket } from './utils/socket';
import {
  applyTournamentNameListSocketEvent,
  clearTournamentNameListCache,
  ensureTournamentNameListLoaded,
} from './utils/tournamentNameListCache';
import { bindPlayerRosterSocket, clearMembersRosterCache } from './utils/membersRosterCache';
import { getSystemConfig, loadPublicSystemConfig, subscribeToSystemConfig, hasAnyPublicAchievementEnabled } from './utils/systemConfig';
import { todayHoursHeaderLabel } from './utils/clubHoursDisplay';
import { clearAllScrollPositions, clearAllUIStates } from './utils/scrollPosition';
import { getErrorMessage } from './utils/errorHandler';
import { loadLastTournamentId, loadShouldRestoreDetail, saveShouldRestoreDetail } from './utils/tournamentNavState';
import { lazyWithReload } from './utils/lazyWithReload';
import { SmashWhizzLogo } from './components/SmashWhizzLogo';
import { AppHeaderCollapsibleControls } from './components/AppHeaderCollapsibleControls';
import { allowedLessonsViews } from './components/lessons/lessonsNav';
import { AppHeaderHostOnDuty } from './components/AppHeaderHostOnDuty';
import { LandscapeRequiredOverlay } from './components/LandscapeRequiredOverlay';
import { APP_NAME_TM } from './brand';
import {
  clearPreferFullApp,
  defaultAuthenticatedPath,
  shouldShowMeReturnLink,
} from './utils/meMode';

// Lazy load route components for code splitting (auto-reload once if deploy invalidated chunks)
const Players = lazyWithReload(() => import('./components/Players'));
const Tournaments = lazyWithReload(() => import('./components/Tournaments'));
const TournamentDetailPage = lazyWithReload(() => import('./components/TournamentDetailPage'));
const Statistics = lazyWithReload(() => import('./components/Statistics'));
const History = lazyWithReload(() => import('./components/History'));
const TournamentRegistrationLink = lazyWithReload(() => import('./components/TournamentRegistrationLink'));
const SystemSettings = lazyWithReload(() => import('./components/SystemSettings'));
const PaymentsAdmin = lazyWithReload(() => import('./components/PaymentsAdmin'));
const AttendanceLogAdmin = lazyWithReload(() => import('./components/AttendanceLogAdmin'));
const MembershipLogAdmin = lazyWithReload(() => import('./components/MembershipLogAdmin'));
const HostsAdmin = lazyWithReload(() => import('./components/HostsAdmin'));
const LessonsPage = lazyWithReload(() => import('./components/lessons/LessonsPage'));
const PublicCoachPage = lazyWithReload(() => import('./components/lessons/PublicCoachPage'));
const PublicClassPage = lazyWithReload(() => import('./components/lessons/PublicClassPage'));
const GroupCoachInvitePage = lazyWithReload(() => import('./components/lessons/GroupCoachInvitePage'));
const PaymentReturnPage = lazyWithReload(() => import('./components/PaymentReturnPage'));
const PublicResultsListPage = lazyWithReload(() => import('./components/public/PublicResultsListPage'));
const PublicResultsLatestPage = lazyWithReload(() =>
  import('./components/public/PublicResultsPages').then((m) => ({ default: m.PublicResultsLatestPage })),
);
const PublicResultsDetailPage = lazyWithReload(() =>
  import('./components/public/PublicResultsPages').then((m) => ({ default: m.PublicResultsDetailPage })),
);
const PublicAchievementsPage = lazyWithReload(() => import('./components/public/PublicAchievementsPage'));
const PublicPresentPage = lazyWithReload(() => import('./components/public/PublicPresentPage'));
const PublicJoinPage = lazyWithReload(() =>
  import('./components/public/PublicJoinPages').then((m) => ({ default: m.PublicJoinPage })),
);
const PublicJoinSetupPage = lazyWithReload(() =>
  import('./components/public/PublicJoinPages').then((m) => ({ default: m.PublicJoinSetupPage })),
);
const PublicJoinDenyPage = lazyWithReload(() =>
  import('./components/public/PublicJoinPages').then((m) => ({ default: m.PublicJoinDenyPage })),
);
const MePage = lazyWithReload(() => import('./components/me/MePage'));

function isLessonPublicPath(pathname: string): boolean {
  return (
    pathname.startsWith('/coaches/') ||
    pathname.startsWith('/classes/') ||
    pathname.startsWith('/group-invites/')
  );
}

function isUnauthenticatedPublicPath(pathname: string): boolean {
  return (
    pathname.startsWith('/tournament-registration/') ||
    pathname === '/public' ||
    pathname.startsWith('/public/') ||
    isLessonPublicPath(pathname)
  );
}

/** Only render detail for positive numeric ids; otherwise bounce to the list. */
function TournamentDetailGate() {
  const { id } = useParams<{ id: string }>();
  const parsed = id != null ? parseInt(id, 10) : NaN;
  if (!Number.isFinite(parsed) || parsed <= 0 || String(parsed) !== id) {
    return <Navigate to="/tournaments" replace />;
  }
  return <TournamentDetailPage />;
}

// Component to prevent default scroll restoration for routes that handle their own scroll
function ScrollToTop() {
  const location = useLocation();
  
  useEffect(() => {
    // Only scroll to top if we're not restoring scroll position
    // This prevents React Router's default scroll restoration
    if (!location.state?.restoreScroll) {
      window.scrollTo(0, 0);
    }
  }, [location.pathname]);
  
  return null;
}

/** Static HTML in public/role-tutorials/ — avoid SPA sending these paths to /players. */
function isRoleTutorialsPath(pathname: string): boolean {
  return pathname === '/role-tutorials' || pathname.startsWith('/role-tutorials/');
}

/**
 * Visiting /role-tutorials (no file) loads the React app first; bounce once to the real static file
 * so public/role-tutorials/index.html is served by Vite (or express static in production).
 */
function RoleTutorialsSpaRedirect() {
  const location = useLocation();
  useEffect(() => {
    if (location.pathname === '/role-tutorials' || location.pathname === '/role-tutorials/') {
      window.location.replace('/role-tutorials/index.html');
    }
  }, [location.pathname]);
  return null;
}

// Component to handle navigation to default home on initial auth
function AuthRedirect() {
  const location = useLocation();
  const navigate = useNavigate();
  
  useEffect(() => {
    if (isRoleTutorialsPath(location.pathname)) return;
    const validPaths = [
      '/me',
      '/players',
      '/tournaments',
      '/statistics',
      '/history',
      '/system-settings',
      '/payments',
      '/attendance-log',
      '/membership-log',
      '/hosts',
      '/lessons',
      '/coach',
    ];
    const isTournamentDetail = /^\/tournaments\/\d+$/.test(location.pathname);
    const isLessonPublic = isLessonPublicPath(location.pathname);
    if (location.pathname.startsWith('/tournaments/') && !isTournamentDetail) {
      navigate('/tournaments', { replace: true });
      return;
    }
    if (!validPaths.includes(location.pathname) && !isTournamentDetail && !isLessonPublic) {
      navigate(defaultAuthenticatedPath(getMember()), { replace: true });
    }
  }, [location.pathname, navigate]);
  
  return null;
}

function App() {
  // Start with false - let useEffect verify authentication
  const [isAuth, setIsAuth] = useState(false);
  const [showPasswordReset, setShowPasswordReset] = useState(false);
  const [isCheckingAuth, setIsCheckingAuth] = useState(true);
  const [clubName, setClubName] = useState<string | null>(null);
  const [authExpiredMessage, setAuthExpiredMessage] = useState<string | null>(null);

  useEffect(() => {
    return subscribeAuthExpired((message) => {
      clearPreferFullApp();
      setIsAuth(false);
      setShowPasswordReset(false);
      setIsCheckingAuth(false);
      setAuthExpiredMessage(message);
    });
  }, []);

  useEffect(() => {
    let cancelled = false;
    loadPublicSystemConfig()
      .then((res) => {
        if (cancelled) return;
        const name = res.branding?.clubName;
        setClubName(typeof name === 'string' && name.trim() !== '' ? name.trim() : null);
      })
      .catch(() => {
        if (!cancelled) setClubName(null);
      });
    const unsubscribe = subscribeToSystemConfig((config) => {
      if (cancelled) return;
      const name = config.branding?.clubName;
      setClubName(typeof name === 'string' && name.trim() !== '' ? name.trim() : null);
    });
    return () => {
      cancelled = true;
      unsubscribe();
    };
  }, []);

  useEffect(() => {
    if (!isAuth) return;
    const socket = connectSocket();
    const refreshConfig = () => {
      void loadPublicSystemConfig();
    };
    socket?.on('system:configUpdated', refreshConfig);
    return () => {
      socket?.off('system:configUpdated', refreshConfig);
    };
  }, [isAuth]);

  // Lean tournament name list for list-page autocomplete: load once per session, patch via sockets.
  useEffect(() => {
    if (!isAuth) {
      clearTournamentNameListCache();
      clearMembersRosterCache();
      return;
    }
    void ensureTournamentNameListLoaded().catch((err) => {
      console.error('Failed to load tournament name list', err);
    });
    const socket = connectSocket();
    const onCreated = (data: any) => applyTournamentNameListSocketEvent('tournament:created', data || {});
    const onUpdated = (data: any) => applyTournamentNameListSocketEvent('tournament:updated', data || {});
    const onStateChanged = (data: any) => applyTournamentNameListSocketEvent('tournament:stateChanged', data || {});
    const onDeleted = (data: any) => applyTournamentNameListSocketEvent('tournament:deleted', data || {});
    socket?.on('tournament:created', onCreated);
    socket?.on('tournament:updated', onUpdated);
    socket?.on('tournament:stateChanged', onStateChanged);
    socket?.on('tournament:deleted', onDeleted);
    const unbindPlayerRoster = bindPlayerRosterSocket(socket);
    return () => {
      socket?.off('tournament:created', onCreated);
      socket?.off('tournament:updated', onUpdated);
      socket?.off('tournament:stateChanged', onStateChanged);
      socket?.off('tournament:deleted', onDeleted);
      unbindPlayerRoster();
    };
  }, [isAuth]);

  useEffect(() => {
    const resetParams = new URLSearchParams(window.location.search);
    const isResetLinkFlow = resetParams.get('reset') === '1' && !!resetParams.get('token');

    if (isResetLinkFlow) {
      // Never reuse stale login state when arriving via reset link.
      removeMember();
      removeToken();
      localStorage.clear();
      sessionStorage.clear();
      setIsAuth(false);
      setShowPasswordReset(false);
      setIsCheckingAuth(false);
      return;
    }

    // Check authentication status on mount
    let isMounted = true;
    let timeoutCleared = false;
    
    const checkAuth = async () => {
      try {
        // If we have a member in localStorage, verify session is still valid
        const member = getMember();
        if (member) {
          try {
            const response = await api.get('/auth/member/me');
            if (!isMounted) return;
            
            if (response.data.member) {
              setIsAuth(true);
              // Check if password reset is required
              if (response.data.member.mustResetPassword) {
                setShowPasswordReset(true);
              }
              setIsCheckingAuth(false);
              timeoutCleared = true;
              return;
            }
          } catch (err: any) {
            // Session expired, invalid, or timeout - clear member and token
            if (!isMounted) return;
            console.error('Auth check failed:', err.message || err);
            const expiredMessage =
              (typeof err?.response?.data?.error === 'string' && err.response.data.error) ||
              consumeAuthExpiredMessage() ||
              'Your session has expired. Please log in again.';
            removeMember();
            removeToken();
            setIsAuth(false);
            setAuthExpiredMessage(expiredMessage);
            setIsCheckingAuth(false);
            timeoutCleared = true;
            return;
          }
        }
        
        // Check token-based auth (for backward compatibility)
        if (!isMounted) return;
        const token = getToken();
        if (token) {
          setIsAuth(true);
        } else {
          setIsAuth(false);
        }
        setIsCheckingAuth(false);
        timeoutCleared = true;
        
      } catch (error) {
        if (!isMounted) return;
        console.error('Unexpected error during auth check:', error);
        setIsAuth(false);
        setIsCheckingAuth(false);
        timeoutCleared = true;
      }
    };
    
    // Add a timeout fallback in case the API call hangs
    const timeoutId = setTimeout(() => {
      if (!timeoutCleared && isMounted) {
        console.warn('Auth check timed out, proceeding without auth');
        setIsCheckingAuth(false);
        setIsAuth(false);
      }
    }, 8000); // Reduced timeout to 8 seconds
    
    checkAuth().finally(() => {
      if (timeoutId) {
        clearTimeout(timeoutId);
        timeoutCleared = true;
      }
    });
    
    return () => {
      isMounted = false;
      if (timeoutId) {
        clearTimeout(timeoutId);
        timeoutCleared = true;
      }
    };
  }, []);

  const handleLogin = async () => {
    setAuthExpiredMessage(null);
    // After successful login, verify the session is properly established
    try {
      const response = await api.get('/auth/member/me');
      if (response.data.member) {
        // Update member data in case it changed
        setMember(response.data.member);
        // Check if password reset is required
        if (response.data.member.mustResetPassword) {
          setShowPasswordReset(true);
        }
        setIsAuth(true);
      } else {
        // Session not established, stay on login
        removeMember();
        removeToken();
        setIsAuth(false);
      }
    } catch (err) {
      // Session verification failed, stay on login
      console.error('Session verification failed after login:', err);
      removeMember();
      removeToken();
      setIsAuth(false);
    }
  };

  const handleLogout = async () => {
    try {
      // Try to logout from session if member is logged in
      if (getMember()) {
        await api.post('/auth/member/logout');
      }
    } catch (err) {
      // Ignore errors, continue with cleanup
    }
    clearPreferFullApp();
    clearTournamentNameListCache();
    clearMembersRosterCache();
    removeToken();
    removeMember();
    setIsAuth(false);
  };

  return (
    <Router>
      <ScrollToTop />
      <RoleTutorialsSpaRedirect />
      <AppRoutes
        isCheckingAuth={isCheckingAuth}
        isAuth={isAuth}
        clubName={clubName}
        authExpiredMessage={authExpiredMessage}
        handleLogin={handleLogin}
        handleLogout={handleLogout}
        showPasswordReset={showPasswordReset}
        setShowPasswordReset={setShowPasswordReset}
        setMember={setMember}
      />
    </Router>
  );
}

function AppRoutes({
  isCheckingAuth,
  isAuth,
  clubName,
  authExpiredMessage,
  handleLogin,
  handleLogout,
  showPasswordReset,
  setShowPasswordReset,
  setMember,
}: {
  isCheckingAuth: boolean;
  isAuth: boolean;
  clubName: string | null;
  authExpiredMessage: string | null;
  handleLogin: () => void | Promise<void>;
  handleLogout: () => void;
  showPasswordReset: boolean;
  setShowPasswordReset: (show: boolean) => void;
  setMember: (member: any) => void;
}) {
  const location = useLocation();
  const lessonPublic = isLessonPublicPath(location.pathname);
  const publicPath = isUnauthenticatedPublicPath(location.pathname) && !(lessonPublic && isAuthenticated());

  if (!publicPath && isCheckingAuth) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <div>Loading...</div>
      </div>
    );
  }

  const lessonPublicElement = (page: ReactNode) => (
    <ErrorBoundary>
      <Suspense fallback={<div>Loading...</div>}>{page}</Suspense>
    </ErrorBoundary>
  );

  return (
    <Routes>
      {!isAuth ? (
        <Route path="/coaches/:id" element={lessonPublicElement(<PublicCoachPage />)} />
      ) : null}
      {!isAuth ? (
        <Route path="/classes/:code" element={lessonPublicElement(<PublicClassPage />)} />
      ) : null}
      {!isAuth ? (
        <Route path="/group-invites/:token" element={lessonPublicElement(<GroupCoachInvitePage />)} />
      ) : null}
      <Route
        path="/tournament-registration/:code"
        element={
          <ErrorBoundary>
            <Suspense fallback={<div>Loading...</div>}>
              <TournamentRegistrationLink />
            </Suspense>
          </ErrorBoundary>
        }
      />
      <Route
        path="/payment-return"
        element={
          <ErrorBoundary>
            <Suspense fallback={<div>Loading...</div>}>
              <PaymentReturnPage />
            </Suspense>
          </ErrorBoundary>
        }
      />
      <Route path="/public" element={<Navigate to="/public/achievements" replace />} />
      <Route path="/public/" element={<Navigate to="/public/achievements" replace />} />
      <Route
        path="/public/results/list"
        element={
          <ErrorBoundary>
            <Suspense fallback={<div>Loading...</div>}>
              <PublicResultsListPage />
            </Suspense>
          </ErrorBoundary>
        }
      />
      <Route path="/public/results" element={<Navigate to="/public/results/latest" replace />} />
      <Route
        path="/public/results/latest"
        element={
          <ErrorBoundary>
            <Suspense fallback={<div>Loading...</div>}>
              <PublicResultsLatestPage />
            </Suspense>
          </ErrorBoundary>
        }
      />
      <Route
        path="/public/results/:id"
        element={
          <ErrorBoundary>
            <Suspense fallback={<div>Loading...</div>}>
              <PublicResultsDetailPage />
            </Suspense>
          </ErrorBoundary>
        }
      />
      <Route
        path="/public/join/setup"
        element={
          <ErrorBoundary>
            <Suspense fallback={<div>Loading...</div>}>
              <PublicJoinSetupPage />
            </Suspense>
          </ErrorBoundary>
        }
      />
      <Route
        path="/public/join/deny"
        element={
          <ErrorBoundary>
            <Suspense fallback={<div>Loading...</div>}>
              <PublicJoinDenyPage />
            </Suspense>
          </ErrorBoundary>
        }
      />
      <Route
        path="/public/join"
        element={
          <ErrorBoundary>
            <Suspense fallback={<div>Loading...</div>}>
              <PublicJoinPage />
            </Suspense>
          </ErrorBoundary>
        }
      />
      <Route
        path="/public/present"
        element={
          <ErrorBoundary>
            <Suspense fallback={<div>Loading...</div>}>
              <PublicPresentPage />
            </Suspense>
          </ErrorBoundary>
        }
      />
      <Route
        path="/public/achievements"
        element={
          <ErrorBoundary>
            <Suspense fallback={<div>Loading...</div>}>
              <PublicAchievementsPage />
            </Suspense>
          </ErrorBoundary>
        }
      />
      <Route
        path="/me"
        element={
          !isAuth ? (
            <ErrorBoundary>
              <Login onLogin={handleLogin} clubName={clubName} initialMessage={authExpiredMessage} />
            </ErrorBoundary>
          ) : (
            <>
              <ErrorBoundary>
                <Suspense
                  fallback={
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        height: '100vh',
                        fontSize: '18px',
                      }}
                    >
                      Loading...
                    </div>
                  }
                >
                  <MePage clubName={clubName} onLogout={handleLogout} />
                </Suspense>
              </ErrorBoundary>
              {showPasswordReset && (
                <PasswordResetModal
                  onPasswordChanged={async () => {
                    try {
                      const response = await api.get('/auth/member/me');
                      if (response.data.member) {
                        setMember(response.data.member);
                        setShowPasswordReset(false);
                      }
                    } catch (err) {
                      setShowPasswordReset(false);
                    }
                  }}
                />
              )}
            </>
          )
        }
      />
      <Route
        path="*"
        element={
          !isAuth ? (
            <ErrorBoundary>
              <Login onLogin={handleLogin} clubName={clubName} initialMessage={authExpiredMessage} />
            </ErrorBoundary>
          ) : (
            <>
              <AuthRedirect />
              <LandscapeRequiredOverlay />
              <div className="container">
                <Header onLogout={handleLogout} clubName={clubName}>
                  <ErrorBoundary>
                    <Suspense
                      fallback={
                        <div
                          style={{
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            height: '50vh',
                            fontSize: '18px',
                          }}
                        >
                          Loading...
                        </div>
                      }
                    >
                      <Routes>
                        <Route
                          path="/"
                          element={<Navigate to={defaultAuthenticatedPath(getMember())} replace />}
                        />
                        <Route path="/players" element={<Players />} />
                        <Route path="/tournaments" element={<Tournaments />} />
                        <Route path="/tournaments/:id" element={<TournamentDetailGate />} />
                        <Route path="/statistics" element={<Statistics />} />
                        <Route path="/history" element={<History />} />
                        <Route path="/system-settings" element={<SystemSettings />} />
                        <Route path="/payments" element={<PaymentsAdmin />} />
                        <Route path="/attendance-log" element={<AttendanceLogAdmin />} />
                        <Route path="/membership-log" element={<MembershipLogAdmin />} />
                        <Route path="/hosts" element={<HostsAdmin />} />
                        <Route path="/lessons" element={<LessonsPage />} />
                        <Route path="/coach" element={<Navigate to="/lessons?view=calendar" replace />} />
                        <Route path="/coaches/:id" element={<PublicCoachPage />} />
                        <Route path="/classes/:code" element={<PublicClassPage />} />
                        <Route path="/group-invites/:token" element={<GroupCoachInvitePage />} />
                      </Routes>
                    </Suspense>
                  </ErrorBoundary>
                </Header>
              </div>

              {showPasswordReset && (
                <PasswordResetModal
                  onPasswordChanged={async () => {
                    try {
                      const response = await api.get('/auth/member/me');
                      if (response.data.member) {
                        setMember(response.data.member);
                        setShowPasswordReset(false);
                      }
                    } catch (err) {
                      setShowPasswordReset(false);
                    }
                  }}
                />
              )}
            </>
          )
        }
      />
    </Routes>
  );
}

// Password Reset Modal Component
function PasswordResetModal({ onPasswordChanged }: { onPasswordChanged: () => void }) {
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [currentPassword, setCurrentPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  /** False when DB password is unset (admin reset / invite) — matches server change-password behavior. */
  const [needsCurrentPassword, setNeedsCurrentPassword] = useState(true);
  const [minimumPasswordLength, setMinimumPasswordLength] = useState(() => getSystemConfig().authPolicy.minimumPasswordLength);

  useEffect(() => {
    let cancelled = false;
    const sync = async () => {
      try {
        const res = await api.get('/auth/member/me');
        const hp = res.data?.member?.hasPassword as boolean | undefined;
        if (!cancelled && typeof hp === 'boolean') {
          setNeedsCurrentPassword(hp);
        }
      } catch {
        const m = getMember();
        if (!cancelled && typeof m?.hasPassword === 'boolean') {
          setNeedsCurrentPassword(m.hasPassword);
        }
      }
    };
    sync();
    const unsubscribe = subscribeToSystemConfig((config) => {
      setMinimumPasswordLength(config.authPolicy.minimumPasswordLength);
    });
    return () => {
      cancelled = true;
      unsubscribe();
    };
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (needsCurrentPassword && !currentPassword.trim()) {
      setError('Current password is required');
      return;
    }

    if (!newPassword || !confirmPassword) {
      setError('New password and confirmation are required');
      return;
    }

    if (newPassword.length < minimumPasswordLength) {
      setError(`New password must be at least ${minimumPasswordLength} characters long`);
      return;
    }

    if (newPassword !== confirmPassword) {
      setError('New password and confirmation do not match');
      return;
    }

    setLoading(true);

    try {
      await api.post('/auth/member/change-password', {
        ...(needsCurrentPassword ? { currentPassword } : {}),
        newPassword,
      });

      const memberResponse = await api.get('/auth/member/me');
      if (memberResponse.data.member) {
        setMember(memberResponse.data.member);
      }

      setNewPassword('');
      setConfirmPassword('');
      setCurrentPassword('');
      onPasswordChanged();
    } catch (err: any) {
      setError(getErrorMessage(err, 'Failed to change password'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 10001,
    }}>
      <div className="card" style={{ maxWidth: '400px', width: '90%', position: 'relative' }}>
        <h2>Password Reset Required</h2>
        <p style={{ marginBottom: '20px', color: '#666' }}>
          {needsCurrentPassword
            ? 'Your password must be updated before you can continue.'
            : 'Set a password for your account to continue.'}
        </p>
        <form onSubmit={handleSubmit}>
          {needsCurrentPassword && (
            <div className="form-group">
              <label>Current Password</label>
              <input
                type="password"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                autoFocus
              />
            </div>
          )}
          <div className="form-group">
            <label>New Password</label>
            <input
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              required
              minLength={minimumPasswordLength}
              autoFocus={!needsCurrentPassword}
            />
          </div>
          <div className="form-group">
            <label>Confirm New Password</label>
            <input
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
              minLength={minimumPasswordLength}
            />
          </div>
          {error && <div className="error-message">{error}</div>}
          <button type="submit" disabled={loading} style={{ width: '100%' }}>
            {loading ? 'Changing Password...' : 'Change Password'}
          </button>
        </form>
      </div>
    </div>
  );
}

function Header({
  onLogout,
  clubName,
  children,
}: {
  onLogout: () => void;
  clubName: string | null;
  children?: ReactNode;
}) {
  const navigate = useNavigate();
  const location = useLocation();
  const systemConfig = useSyncExternalStore(subscribeToSystemConfig, getSystemConfig, getSystemConfig);
  const todayHours = todayHoursHeaderLabel(systemConfig.branding);
  const showAchievementsLink = hasAnyPublicAchievementEnabled(systemConfig);
  const [userName, setUserName] = useState<string>('');
  const [userRoles, setUserRoles] = useState<string[]>([]);
  const [kioskMode, setKioskMode] = useState(() => isKioskMode());
  const [kioskKind, setKioskKind] = useState<KioskKind | undefined>(() => getKioskKind());
  const [kioskTournamentId, setKioskTournamentId] = useState<number | undefined>(() => getKioskTournamentId());
  const [showMeReturn, setShowMeReturn] = useState(() => shouldShowMeReturnLink());
  const [showRestoreModal, setShowRestoreModal] = useState(false);
  const [showAboutModal, setShowAboutModal] = useState(false);
  const [restorePassword, setRestorePassword] = useState('');
  const [restoreError, setRestoreError] = useState('');
  const [restoreLoading, setRestoreLoading] = useState(false);
  const [pendingPreregistrationCount, setPendingPreregistrationCount] = useState(0);
  const [adminMenuOpen, setAdminMenuOpen] = useState(false);
  const adminMenuRef = useRef<HTMLDivElement | null>(null);
  const autoRelinquishIdleTimerRef = useRef<number | null>(null);
  const autoRelinquishIdleCleanupRef = useRef<(() => void) | null>(null);
  const changesetId = (import.meta.env.VITE_CHANGESET_ID || 'devbuild').slice(0, 7);
  
  const isPlayersActive = location.pathname === '/players';
  const isTournamentsActive = location.pathname === '/tournaments' || location.pathname.startsWith('/tournaments/');
  const isSettingsActive = location.pathname === '/system-settings';
  const isPaymentsActive = location.pathname === '/payments';
  const isAttendanceActive = location.pathname === '/attendance-log';
  const isMembershipLogActive = location.pathname === '/membership-log';
  const isHostsActive = location.pathname === '/hosts';
  const paymentsTab = new URLSearchParams(location.search).get('tab');
  const isPlansActive = isPaymentsActive && paymentsTab === 'plans';
  const isPaymentsListActive = isPaymentsActive && !isPlansActive;
  const isAdminSectionActive =
    isSettingsActive || isPaymentsActive || isAttendanceActive || isMembershipLogActive || isHostsActive;
  const adminMenuLabel = isSettingsActive
    ? 'System Configuration'
    : isPlansActive
      ? 'Payment Plans'
      : isMembershipLogActive
        ? 'Membership Log'
        : isHostsActive
          ? 'Hosts'
        : isAttendanceActive
          ? 'Attendance Log'
          : isPaymentsListActive
            ? 'Payment Log'
            : 'Admin';
  const isAchievementsActive =
    location.pathname === '/public' ||
    location.pathname === '/public/' ||
    location.pathname.startsWith('/public/achievements');

  // Shared sizing for header icon controls ($ ⚙️) so they align uniformly.
  // Vertical padding matches Players/Tournaments tab rhythm (10 / 12).
  const headerIconControlSize: CSSProperties = {
    padding: '10px 12px 12px 12px',
    minWidth: '44px',
    height: '44px',
    fontSize: '16px',
    lineHeight: 1,
    boxSizing: 'border-box',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    textAlign: 'center',
  };
  
  // Format roles as comma-separated first letters
  const formatRoles = (roles: string[]): string => {
    if (!roles || roles.length === 0) return '';
    return roles
      .map(role => role.charAt(0)) // Get first letter
      .join(', '); // Join with comma and space
  };
  
  useEffect(() => {
    const applyMember = (member: {
      firstName?: string;
      lastName?: string;
      roles?: string[];
      kioskMode?: boolean;
      kioskKind?: KioskKind;
      kioskTournamentId?: number;
    }) => {
      setUserName(`${member.firstName || ''} ${member.lastName || ''}`.trim() || 'User');
      setUserRoles(member.roles || []);
      setKioskMode(member.kioskMode === true);
      setKioskKind(
        member.kioskMode === true &&
          (member.kioskKind === 'checkin' ||
            member.kioskKind === 'browse' ||
            member.kioskKind === 'tournamentScore')
          ? member.kioskKind
          : undefined,
      );
      setKioskTournamentId(
        member.kioskMode === true && typeof member.kioskTournamentId === 'number'
          ? member.kioskTournamentId
          : undefined,
      );
    };

    // Fetch current user info
    const fetchUserInfo = async () => {
      try {
        // Try to get member info from session
        const response = await api.get('/auth/member/me');
        if (response.data.member) {
          const member = response.data.member;
          setMember(member);
          applyMember(member);
          return;
        }
      } catch (err) {
        // If member endpoint fails, try to get from localStorage
        const member = getMember();
        if (member) {
          applyMember(member);
          return;
        }
      }
      
      // Fallback: if we have a token but no member, show "User"
      if (getToken()) {
        setUserName('User');
        setUserRoles([]);
        setKioskMode(false);
        setKioskKind(undefined);
        setKioskTournamentId(undefined);
      }
    };
    
    fetchUserInfo();
    const onKioskChanged = () => {
      const member = getMember();
      if (member) applyMember(member);
    };
    window.addEventListener('kiosk-mode-changed', onKioskChanged);
    return () => window.removeEventListener('kiosk-mode-changed', onKioskChanged);
  }, [location.pathname]);

  useEffect(() => {
    const syncMeReturn = () => setShowMeReturn(shouldShowMeReturnLink());
    syncMeReturn();
    window.addEventListener('resize', syncMeReturn);
    return () => window.removeEventListener('resize', syncMeReturn);
  }, [location.pathname, kioskMode, userRoles]);

  useEffect(() => {
    if (!kioskMode) return;
    if (kioskKind === 'checkin' && location.pathname !== '/players') {
      navigate('/players', { replace: true });
      return;
    }
    if (kioskKind === 'tournamentScore' && kioskTournamentId != null) {
      const expected = `/tournaments/${kioskTournamentId}`;
      if (location.pathname !== expected) {
        navigate(expected, { replace: true });
      }
    }
  }, [kioskMode, kioskKind, kioskTournamentId, location.pathname, navigate]);

  const clearAutoRelinquishIdleTimer = () => {
    if (autoRelinquishIdleTimerRef.current !== null) {
      window.clearTimeout(autoRelinquishIdleTimerRef.current);
      autoRelinquishIdleTimerRef.current = null;
    }
    if (autoRelinquishIdleCleanupRef.current) {
      autoRelinquishIdleCleanupRef.current();
      autoRelinquishIdleCleanupRef.current = null;
    }
  };

  const handleRelinquish = async (kind?: KioskKind, tournamentId?: number) => {
    clearAutoRelinquishIdleTimer();
    try {
      const resolvedKind = kind ?? defaultKioskKindForRoles(getMember()?.roles || userRoles);
      if (!resolvedKind) {
        window.alert('Unable to determine kiosk mode for this account');
        return;
      }
      const path = await enterKioskMode({
        kind: resolvedKind,
        tournamentId,
      });
      const member = getMember();
      if (member) {
        setKioskMode(member.kioskMode === true);
        setKioskKind(getKioskKind());
        setKioskTournamentId(getKioskTournamentId());
        setUserRoles(member.roles || []);
      }
      navigate(path, { replace: true });
    } catch (err: any) {
      window.alert(err.response?.data?.error || 'Failed to enter kiosk mode');
    }
  };

  const scheduleAutoRelinquishIdle = (memberPayload?: {
    autoRelinquishPrivileges?: boolean;
    autoRelinquishIdleMinutes?: number;
  }) => {
    clearAutoRelinquishIdleTimer();
    const member = memberPayload || getMember();
    const enabled = member?.autoRelinquishPrivileges === true;
    const minutes = Number(
      member?.autoRelinquishIdleMinutes ?? getSystemConfig().authPolicy.autoRelinquishIdleMinutes
    );
    if (!enabled || !Number.isFinite(minutes) || minutes <= 0) {
      return;
    }
    const ms = minutes * 60 * 1000;
    const arm = () => {
      if (autoRelinquishIdleTimerRef.current !== null) {
        window.clearTimeout(autoRelinquishIdleTimerRef.current);
      }
      autoRelinquishIdleTimerRef.current = window.setTimeout(() => {
        void handleRelinquish();
      }, ms);
    };
    arm();
    const onActivity = () => arm();
    const events: Array<keyof WindowEventMap> = ['mousemove', 'mousedown', 'keydown', 'touchstart', 'scroll'];
    events.forEach((eventName) => window.addEventListener(eventName, onActivity, { passive: true }));
    autoRelinquishIdleCleanupRef.current = () => {
      events.forEach((eventName) => window.removeEventListener(eventName, onActivity));
    };
  };

  useEffect(() => {
    return () => {
      clearAutoRelinquishIdleTimer();
    };
  }, []);

  const handleRestore = async () => {
    if (!restorePassword.trim()) {
      setRestoreError('Password is required');
      return;
    }
    setRestoreLoading(true);
    setRestoreError('');
    try {
      const response = await api.post('/auth/member/restore-privileges', {
        password: restorePassword,
      });
      if (response.data.token) {
        setToken(response.data.token);
      }
      const me = await api.get('/auth/member/me');
      if (me.data.member) {
        setMember(me.data.member);
        setKioskMode(false);
        setKioskKind(undefined);
        setKioskTournamentId(undefined);
        setUserRoles(me.data.member.roles || []);
        scheduleAutoRelinquishIdle(me.data.member);
      }
      setShowRestoreModal(false);
      setRestorePassword('');
    } catch (err: any) {
      setRestoreError(err.response?.data?.error || 'Failed to restore privileges');
    } finally {
      setRestoreLoading(false);
    }
  };

  useEffect(() => {
    let cancelled = false;
    let deadlineTimeout: number | null = null;
    let hasConnectedOnce = false;

    const scheduleDeadlineRefresh = (nextDeadlineAt: unknown) => {
      if (deadlineTimeout !== null) {
        window.clearTimeout(deadlineTimeout);
        deadlineTimeout = null;
      }
      if (typeof nextDeadlineAt !== 'string' || nextDeadlineAt.trim() === '') return;

      const deadlineTime = new Date(nextDeadlineAt).getTime();
      if (!Number.isFinite(deadlineTime)) return;

      const delay = Math.max(0, deadlineTime - Date.now() + 1000);
      deadlineTimeout = window.setTimeout(() => {
        void fetchPendingPreregistrations();
      }, delay);
    };

    const fetchPendingPreregistrations = async () => {
      try {
        const response = await api.get('/tournaments/preregistration/pending-count');
        if (!cancelled) {
          setPendingPreregistrationCount(Number(response.data?.count) || 0);
          scheduleDeadlineRefresh(response.data?.nextDeadlineAt);
        }
      } catch {
        if (!cancelled) {
          setPendingPreregistrationCount(0);
          scheduleDeadlineRefresh(null);
        }
      }
    };

    const onSocketConnect = () => {
      // Skip the first connect — mount fetch covers cold start; refetch only after reconnect.
      if (hasConnectedOnce) {
        void fetchPendingPreregistrations();
      }
      hasConnectedOnce = true;
    };

    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        void fetchPendingPreregistrations();
      }
    };

    void fetchPendingPreregistrations();
    const socket = connectSocket();
    socket?.on('connect', onSocketConnect);
    if (socket?.connected) {
      hasConnectedOnce = true;
    }
    socket?.on('preregistration:changed', fetchPendingPreregistrations);
    window.addEventListener('tournament-preregistration-count-changed', fetchPendingPreregistrations);
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => {
      cancelled = true;
      if (deadlineTimeout !== null) {
        window.clearTimeout(deadlineTimeout);
      }
      socket?.off('connect', onSocketConnect);
      socket?.off('preregistration:changed', fetchPendingPreregistrations);
      window.removeEventListener('tournament-preregistration-count-changed', fetchPendingPreregistrations);
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, []);
  
  const handlePlayersClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    clearAllScrollPositions();
    clearAllUIStates();
    window.scrollTo(0, 0);
    navigate('/players', { replace: true });
  };

  const handleTournamentsClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    const lastId = loadLastTournamentId();
    if (loadShouldRestoreDetail() && lastId != null && Number.isFinite(lastId) && lastId > 0) {
      navigate(`/tournaments/${lastId}`, { replace: true });
      return;
    }
    saveShouldRestoreDetail(false);
    navigate('/tournaments', { replace: true });
  };

  const handleSettingsClick = (e?: React.MouseEvent) => {
    e?.preventDefault();
    setAdminMenuOpen(false);
    clearAllScrollPositions();
    clearAllUIStates();
    window.scrollTo(0, 0);
    navigate('/system-settings', { replace: true });
  };

  const handlePaymentsClick = (e?: React.MouseEvent, tab: 'payments' | 'plans' = 'payments') => {
    e?.preventDefault();
    setAdminMenuOpen(false);
    clearAllScrollPositions();
    clearAllUIStates();
    window.scrollTo(0, 0);
    navigate(tab === 'plans' ? '/payments?tab=plans' : '/payments', { replace: true });
  };

  const handleAttendanceClick = (e?: React.MouseEvent) => {
    e?.preventDefault();
    setAdminMenuOpen(false);
    clearAllScrollPositions();
    clearAllUIStates();
    window.scrollTo(0, 0);
    navigate('/attendance-log', { replace: true });
  };

  const handleMembershipLogClick = (e?: React.MouseEvent) => {
    e?.preventDefault();
    setAdminMenuOpen(false);
    clearAllScrollPositions();
    clearAllUIStates();
    window.scrollTo(0, 0);
    navigate('/membership-log', { replace: true });
  };

  const handleHostsClick = (e?: React.MouseEvent) => {
    e?.preventDefault();
    setAdminMenuOpen(false);
    clearAllScrollPositions();
    clearAllUIStates();
    window.scrollTo(0, 0);
    navigate('/hosts', { replace: true });
  };

  useEffect(() => {
    if (!adminMenuOpen) return;
    const onPointerDown = (event: MouseEvent) => {
      const target = event.target as Node | null;
      if (
        (adminMenuRef.current && target && adminMenuRef.current.contains(target)) ||
        (target instanceof Element && target.closest('[data-admin-menu]'))
      ) {
        return;
      }
      setAdminMenuOpen(false);
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setAdminMenuOpen(false);
    };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [adminMenuOpen]);

  const adminMenuItems = [
    { id: 'payment-log' as const, label: 'Payment Log', active: isPaymentsListActive },
    { id: 'attendance-log' as const, label: 'Attendance Log', active: isAttendanceActive },
    { id: 'membership-log' as const, label: 'Membership Log', active: isMembershipLogActive },
    { id: 'separator' as const, label: '', active: false },
    { id: 'hosts' as const, label: 'Hosts', active: isHostsActive },
    { id: 'plans' as const, label: 'Payment Plans', active: isPlansActive },
    { id: 'settings' as const, label: 'System Configuration', active: isSettingsActive },
  ] as const;
  const adminMenuLongestLabel = adminMenuItems.reduce(
    (longest, item) => (item.label.length > longest.length ? item.label : longest),
    'Admin',
  );
  // Keep trigger + every dropdown option the same width (longest label).
  const adminMenuWidth = `calc(${adminMenuLongestLabel.length + 2}ch + 24px)`;

  const hasPendingPreregistrations = pendingPreregistrationCount > 0;
  const isAdminUser = !kioskMode && isAdmin();
  const showPlayersTab = !kioskMode || kioskKind === 'browse' || kioskKind === 'checkin';
  const showTournamentsTab = !kioskMode || kioskKind === 'browse';
  const showInstructions = !kioskMode && allowedLessonsViews({
    coach: hasMemberRole('COACH'),
    player: hasMemberRole('PLAYER'),
    admin: isAdmin(),
  }).length > 0;
  const kioskBannerText =
    kioskKind === 'checkin'
      ? 'KIOSK MODE — Club check-in / check-out. Find your name, then enter your PIN.'
      : kioskKind === 'tournamentScore'
        ? 'KIOSK MODE — Tournament score entry. Enter match scores with participant PINs.'
        : 'KIOSK MODE — Browse. View players, tournaments, statistics, and history.';
  
  return (
    <>
    {(clubName || userName) ? (
      <div className="app-header-meta">
        <div className="app-header-meta-about">
          <button
            type="button"
            className="app-header-club"
            onClick={() => setShowAboutModal(true)}
            aria-haspopup="dialog"
            aria-label={`About ${APP_NAME_TM}`}
            style={{
              display: 'inline-block',
              boxSizing: 'border-box',
              padding: '6px 16px',
              backgroundColor: '#2c3e50',
              color: 'white',
              borderRadius: '999px',
              border: 'none',
              fontSize: '17px',
              fontWeight: 600,
              letterSpacing: '0.02em',
              lineHeight: 1.25,
              whiteSpace: 'nowrap',
              cursor: 'pointer',
              fontFamily: 'inherit',
            }}
          >
            About
          </button>
        </div>
        <div className="app-header-meta-club">
          {clubName ? (
            <span
              className="app-header-club app-header-club-caption"
              style={{
                display: 'inline-block',
                boxSizing: 'border-box',
                maxWidth: '100%',
                padding: '6px 16px',
                backgroundColor: '#2c3e50',
                color: 'white',
                borderRadius: '999px',
                fontSize: '17px',
                fontWeight: 600,
                letterSpacing: '0.02em',
                lineHeight: 1.25,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
              title={`${clubName} (${todayHours.label}${todayHours.comment ? ` · ${todayHours.comment}` : ''})`}
            >
              {clubName}
              <span
                style={{
                  fontSize: '12px',
                  fontWeight: 500,
                  color: 'rgba(255, 255, 255, 0.8)',
                  letterSpacing: '0.01em',
                  marginLeft: '0.35em',
                }}
              >
                ({todayHours.label}
                {todayHours.comment ? ` · ${todayHours.comment}` : ''})
              </span>
            </span>
          ) : null}
          <AppHeaderHostOnDuty />
        </div>
        <div className="app-header-meta-user">
          {userName ? (
            <span
              className="app-header-club"
              style={{
                display: 'inline-block',
                boxSizing: 'border-box',
                padding: '6px 16px',
                backgroundColor: '#2c3e50',
                color: 'white',
                borderRadius: '999px',
                fontSize: '17px',
                fontWeight: 600,
                letterSpacing: '0.02em',
                lineHeight: 1.25,
                whiteSpace: 'nowrap',
              }}
              title={userName}
            >
              {userName}
              {userRoles.length > 0 && !kioskMode ? (
                <span
                  style={{
                    fontSize: '12px',
                    fontWeight: 500,
                    color: 'rgba(255, 255, 255, 0.8)',
                    letterSpacing: '0.01em',
                    marginLeft: '0.35em',
                  }}
                >
                  ({formatRoles(userRoles)})
                </span>
              ) : null}
              {kioskMode ? (
                <span
                  style={{
                    fontSize: '12px',
                    fontWeight: 500,
                    color: 'rgba(255, 255, 255, 0.8)',
                    letterSpacing: '0.01em',
                    marginLeft: '0.35em',
                  }}
                >
                  (Kiosk{kioskKind ? `: ${kioskKind}` : ''})
                </span>
              ) : null}
            </span>
          ) : null}
        </div>
      </div>
    ) : null}
    <div className="header" style={{
      position: 'sticky',
      top: 0,
      zIndex: 10000,
      boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
      ...(kioskMode ? { marginBottom: '10px' } : {})
    }}>
      <div
        className="app-header-row"
        data-collapsible-actions-boundary
        style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        width: '100%',
        gap: '15px',
        paddingBottom: '0',
      }}>
        <div className="app-header-logo-fixed">
        <h1
          className="app-header-title"
          style={{
          margin: 0,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-start',
          justifyContent: 'flex-start',
          flex: '0 0 auto',
        }}>
          <div className="app-header-logo-wrap">
            <SmashWhizzLogo />
          </div>
        </h1>
        </div>
        <AppHeaderCollapsibleControls
          headerIconControlSize={headerIconControlSize}
          showPlayersTab={showPlayersTab}
          showTournamentsTab={showTournamentsTab}
          showInstructions={showInstructions}
          isAdminUser={isAdminUser}
          kioskMode={kioskMode}
          userName={userName}
          showMeReturn={showMeReturn}
          showAchievementsLink={showAchievementsLink}
          isPlayersActive={isPlayersActive}
          isTournamentsActive={isTournamentsActive}
          isAdminSectionActive={isAdminSectionActive}
          isAchievementsActive={isAchievementsActive}
          hasPendingPreregistrations={hasPendingPreregistrations}
          pendingPreregistrationCount={pendingPreregistrationCount}
          adminMenuLabel={adminMenuLabel}
          adminMenuWidth={adminMenuWidth}
          adminMenuOpen={adminMenuOpen}
          setAdminMenuOpen={setAdminMenuOpen}
          adminMenuRef={adminMenuRef}
          adminMenuItems={adminMenuItems}
          onPlayersClick={handlePlayersClick}
          onTournamentsClick={handleTournamentsClick}
          onSettingsClick={handleSettingsClick}
          onPaymentsClick={handlePaymentsClick}
          onAttendanceClick={handleAttendanceClick}
          onHostsClick={handleHostsClick}
          onMembershipLogClick={handleMembershipLogClick}
          onLogout={onLogout}
        />
      </div>
      {kioskMode && (
        <div
          style={{
            background: '#c0392b',
            color: 'white',
            textAlign: 'center',
            padding: '8px 16px',
            marginTop: '8px',
            borderRadius: '6px',
            fontWeight: 700,
            fontSize: '16px',
            letterSpacing: '0.02em',
          }}
        >
          {kioskBannerText}
          <button
            type="button"
            onClick={() => { setShowRestoreModal(true); setRestoreError(''); setRestorePassword(''); }}
            style={{
              marginLeft: '16px',
              padding: '6px 12px',
              border: '1px solid rgba(255,255,255,0.8)',
              background: 'rgba(255,255,255,0.15)',
              color: 'white',
              borderRadius: '4px',
              cursor: 'pointer',
              fontWeight: 600,
            }}
          >
            Restore privileges
          </button>
        </div>
      )}
    </div>
    {children}
    {showRestoreModal && (
      <div
        style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(0,0,0,0.55)',
          zIndex: 20000,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
        onClick={() => !restoreLoading && setShowRestoreModal(false)}
      >
        <div
          style={{
            background: 'white',
            padding: '24px',
            borderRadius: '8px',
            width: '90%',
            maxWidth: '400px',
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <h3 style={{ marginTop: 0 }}>Restore privileges</h3>
          <p style={{ fontSize: '14px', color: '#555' }}>
            Enter your password to leave kiosk mode and restore organizer/admin privileges.
          </p>
          {restoreError && (
            <div style={{ color: '#c0392b', marginBottom: '10px', fontSize: '14px' }}>{restoreError}</div>
          )}
          <input
            type="password"
            value={restorePassword}
            onChange={(e) => setRestorePassword(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                handleRestore();
              }
            }}
            placeholder="Password"
            autoFocus
            style={{ width: '100%', padding: '10px', marginBottom: '16px', boxSizing: 'border-box' }}
          />
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
            <button type="button" onClick={() => setShowRestoreModal(false)} disabled={restoreLoading}>
              Cancel
            </button>
            <button type="button" onClick={handleRestore} disabled={restoreLoading} className="success">
              {restoreLoading ? 'Restoring…' : 'Restore'}
            </button>
          </div>
        </div>
      </div>
    )}
    {showAboutModal && (
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="about-modal-title"
        style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(15, 32, 48, 0.62)',
          zIndex: 20000,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '16px',
        }}
        onClick={() => setShowAboutModal(false)}
      >
        <div
          style={{
            background: 'linear-gradient(165deg, #ffffff 0%, #f4f8fb 100%)',
            borderRadius: '14px',
            width: '100%',
            maxWidth: '400px',
            overflow: 'hidden',
            boxShadow: '0 18px 48px rgba(15, 32, 48, 0.28)',
            border: '1px solid rgba(70, 130, 180, 0.22)',
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <div
            style={{
              background: '#2c3e50',
              padding: '22px 24px 18px',
              color: 'white',
              textAlign: 'center',
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '10px' }}>
              <SmashWhizzLogo variant="about" />
            </div>
            <h3
              id="about-modal-title"
              style={{
                margin: 0,
                fontSize: '22px',
                fontWeight: 700,
                letterSpacing: '0.03em',
              }}
            >
              {APP_NAME_TM}
            </h3>
            <p
              style={{
                margin: '8px 0 0',
                fontSize: '13px',
                lineHeight: 1.4,
                opacity: 0.92,
              }}
            >
              Built for day-by-day operations, tournaments, events — less paperwork, more clarity
            </p>
            <p
              aria-label={`Build ${changesetId}`}
              title={changesetId}
              style={{
                margin: '10px 0 0',
                color: 'rgba(255, 255, 255, 0.55)',
                fontSize: '11px',
                fontFamily: 'monospace',
                letterSpacing: '0.06em',
                lineHeight: 1,
              }}
            >
              {changesetId}
            </p>
          </div>
          <div style={{ padding: '20px 24px 22px' }}>
            <p
              style={{
                margin: '0 0 8px',
                fontSize: '11px',
                fontWeight: 700,
                letterSpacing: '0.08em',
                textTransform: 'uppercase',
                color: '#5F9EA0',
              }}
            >
              Major features
            </p>
            <ul
              style={{
                margin: '0 0 20px',
                padding: '0 0 0 1.15em',
                fontSize: '14px',
                lineHeight: 1.65,
                color: '#2c3e50',
              }}
            >
              <li>Member management</li>
              <li>Tournaments &amp; live scoring</li>
              <li>Player ratings</li>
              <li>Club check-in/check-out</li>
              <li>Payments</li>
              <li>Admissions</li>
            </ul>
            <div
              style={{
                borderTop: '1px solid rgba(44, 62, 80, 0.12)',
                paddingTop: '14px',
                marginBottom: '16px',
                textAlign: 'center',
              }}
            >
              <p style={{ margin: '0 0 4px', fontSize: '14px', color: '#2c3e50', fontWeight: 600 }}>
                Author: Ilya Volvovski
              </p>
              <p style={{ margin: 0, fontSize: '12px', color: '#6a7a8a' }}>
                © {new Date().getFullYear()} Ilya Volvovski. All rights reserved.
              </p>
            </div>
            <div style={{ textAlign: 'center' }}>
              <button
                type="button"
                onClick={() => setShowAboutModal(false)}
                autoFocus
                style={{
                  padding: '9px 28px',
                  background: 'linear-gradient(to bottom, #4682B4 0%, #3a6d96 100%)',
                  color: 'white',
                  border: '1px solid #3a6d96',
                  borderRadius: '999px',
                  cursor: 'pointer',
                  fontWeight: 600,
                  fontSize: '14px',
                  letterSpacing: '0.02em',
                  boxShadow: '0 2px 6px rgba(70, 130, 180, 0.35)',
                }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      </div>
    )}
    </>
  );
}

export default App;


